// Activity: this tenant's change log (with before → after) and its sign-ins.
//
//   changes  GET /platform/audit?league_id=&exclude_super_admin=&cursor=
//   logins   GET /platform/logins?league_id=

import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import type { AuditListResponse } from '@/api/types'
import { AuditEntryList } from '@/components/audit/AuditEntryList'
import { deviceText, failureReasonText } from '@/components/audit/loginText'
import { useCursorPages } from '@/components/audit/useCursorPages'
import { Badge, Button, Card, CardHeader, DataTable, EmptyState, ErrorState, RelativeTime, SkeletonRows, Toggle, type Column } from '@/components/ui'
import { useQuery } from '@/lib/query'
import { useTenant } from '../tenantContext'

const PAGE = 25

interface LoginRow {
  key: string
  created_at: string
  email: string
  success: boolean
  failure_reason: string | null
  ip_address: string | null
  user_agent: string | null
}

export default function ActivityTab() {
  const { tenant } = useTenant()
  return (
    <div className="grid grid-cols-1 gap-4">
      <ChangesCard tenantId={tenant.id} />
      <LoginsCard tenantId={tenant.id} />
    </div>
  )
}

function ChangesCard({ tenantId }: { tenantId: number }) {
  const [hideStaff, setHideStaff] = useState(false)
  const toggleId = useId()
  const { first, entries, nextCursor, loadMore, loadingMore, moreError } = useCursorPages<AuditListResponse['entries'][number], AuditListResponse>(
    ['tenant', tenantId, 'audit', hideStaff],
    (cursor, signal) => api.audit.list({ league_id: tenantId, exclude_super_admin: hideStaff, limit: PAGE, cursor }, signal),
  )

  return (
    <Card flush className="overflow-hidden">
      <div className="px-4 pt-4 sm:px-5">
        <CardHeader
          title="Changes"
          description="Everything changed in this league, newest first, with the value before and after."
          actions={
            <Link to={`/audit/changes?league_id=${tenantId}`} className="text-xs font-semibold text-accent hover:underline">
              Open in Audit
            </Link>
          }
        />
        <div className="mb-3 flex items-center gap-2.5">
          <Toggle size="sm" checked={hideStaff} onChange={setHideStaff} labelledBy={toggleId} />
          <span id={toggleId} className="text-[13px] text-ink">
            Hide ScoreLM staff changes
          </span>
        </div>
      </div>
      <div className="border-t border-line">
        {first.status === 'loading' && !first.data ? (
          <SkeletonRows rows={4} label="Loading changes" />
        ) : first.status === 'error' && !first.data ? (
          <ErrorState error={first.error} title="Couldn't load changes" onRetry={first.refetch} />
        ) : entries.length === 0 ? (
          <EmptyState
            title="No changes yet"
            description={hideStaff ? 'Nobody at the league has changed anything. Turn off “Hide ScoreLM staff changes” to see ours.' : 'Nothing has been changed in this league.'}
          />
        ) : (
          <>
            <AuditEntryList entries={entries} showTenant={false} label="Changes in this league" />
            <MoreFooter nextCursor={nextCursor} loading={loadingMore} error={moreError} onMore={loadMore} shown={entries.length} />
          </>
        )}
      </div>
    </Card>
  )
}

function MoreFooter({ nextCursor, loading, error, onMore, shown }: { nextCursor: number | null; loading: boolean; error: string | null; onMore: () => void; shown: number }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 sm:px-5">
      <p className="text-xs text-muted tabular">
        {shown} shown{nextCursor == null ? ', that’s everything' : ''}
      </p>
      {error ? <p role="alert" className="text-xs text-bad">Couldn&apos;t load more: {error}</p> : null}
      {nextCursor != null ? (
        <Button size="sm" onClick={onMore} loading={loading}>
          Load more
        </Button>
      ) : null}
    </div>
  )
}

const LOGIN_COLUMNS: Column<LoginRow>[] = [
  { id: 'when', header: 'When', cell: (l) => <RelativeTime value={l.created_at} /> },
  { id: 'email', header: 'Email', primary: true, cell: (l) => <span className="break-all">{l.email}</span> },
  {
    id: 'result',
    header: 'Result',
    cell: (l) => (l.success ? <Badge tone="good">Signed in</Badge> : <Badge tone="bad">{failureReasonText(l.failure_reason)}</Badge>),
  },
  { id: 'ip', header: 'IP', cell: (l) => <span className="break-all font-mono text-xs text-muted">{l.ip_address ?? '—'}</span> },
  { id: 'device', header: 'Device', cell: (l) => <span className="text-muted">{deviceText(l.user_agent)}</span>, hideOnMobile: true },
]

function LoginsCard({ tenantId }: { tenantId: number }) {
  const { data, error, status, refetch } = useQuery(['tenant', tenantId, 'logins'], async (signal) => {
    const r = await api.audit.logins({ league_id: tenantId, limit: PAGE }, signal)
    return {
      total: r.summary.total,
      rows: r.entries.map((e): LoginRow => ({ key: `l${e.id}`, created_at: e.created_at, email: e.email, success: e.success, failure_reason: e.failure_reason, ip_address: e.ip_address, user_agent: e.user_agent })),
    }
  })

  return (
    <Card flush className="overflow-hidden">
      <div className="px-4 pt-4 sm:px-5">
        <CardHeader
          title="Sign-ins"
          description="League admins signing in to this league, newest first."
          actions={
            <Link to={`/audit/logins?league_id=${tenantId}`} className="text-xs font-semibold text-accent hover:underline">
              All sign-ins
            </Link>
          }
        />
      </div>
      <div className="border-t border-line">
        {status === 'loading' && !data ? (
          <SkeletonRows rows={3} label="Loading sign-ins" />
        ) : status === 'error' && !data ? (
          <ErrorState error={error} title="Couldn't load sign-ins" onRetry={refetch} />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState title="No sign-ins yet" description="Nobody has signed in to this league's admin." />
        ) : (
          <>
            <DataTable caption="Sign-ins" rows={data.rows} columns={LOGIN_COLUMNS} rowKey={(l) => l.key} />
            {data.total > data.rows.length ? (
              <p className="border-t border-line px-4 py-3 text-xs text-muted sm:px-5">
                Showing the latest {data.rows.length} of {data.total}.{' '}
                <Link to={`/audit/logins?league_id=${tenantId}`} className="font-semibold text-accent hover:underline">
                  See all
                </Link>
              </p>
            ) : null}
          </>
        )}
      </div>
    </Card>
  )
}
